import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import InputField from 'reusableComponents/InputField/InputField'
import PropTypes from 'prop-types'
import { breakpoints, colors } from 'globalStyles'
import { formatPhoneNumber } from 'utils/helperFunctions'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'

const StyledForm = styled(Form)`
  margin-left: 10px;
  margin-right: 10px;
  @media ${breakpoints.desktop} {
    margin-left: 30px;
    margin-right: 30px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 48px;
  justify-content: space-between;
  margin-top: 24px;

  @media ${breakpoints.phone} {
    gap: 16px;
  }
`

const checkEmailValidity = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  return re.test(String(email).toLowerCase())
}

const checkNameValidity = (name) => {
  const re = /^([a-zA-Z]+[\s-])*[a-zA-Z]+$/
  return re.test(String(name).toLowerCase())
}

const checkMobileNumberValidity = (number) => {
  const regex = /^1 \d{3} \d{3} \d{4}$/
  return regex.test(number)
}

const getErrorMessage = (fieldName, fieldValue, t) => {
  if (fieldName === 'firstName' || fieldName === 'lastName') {
    const isNameValid = checkNameValidity(fieldValue)
    if (fieldValue.length === 0) {
      if (fieldName === 'firstName') return t('subscription.first_name_empty_error')
      if (fieldName === 'lastName') return t('subscription.last_name_empty_error')
    }
    if (fieldValue.length > 50 || !isNameValid) {
      return t('subscription.first_name_invalid_error')
    }
  } else if (fieldName === 'mobileNumber') {
    if (fieldValue.length === 0 || !checkMobileNumberValidity(fieldValue)) {
      return t('my_profile.mobile_number_error_msg')
    }
  } else if (fieldName === 'emailAddress') {
    const isEmailValid = checkEmailValidity(fieldValue)
    if (fieldValue.length === 0) {
      return t('subscription.email_invalid_error')
    }
    if (!isEmailValid) {
      return t('my_profile.email_format_error_msg')
    }
  }
  return null
}

const handleSubmit = (e, hasError, onSubmit) => {
  e.preventDefault()
  if (hasError) return

  onSubmit(e)
}

const EditForm = ({ editMode, profileData, onInputChange, onCancel, onSubmit, t, isLoading }) => {
  const [hasError, setHasError] = useState(false)

  const fieldMappings = useMemo(
    () => ({
      fullName: [
        {
          type: 'text',
          key: 'firstName',
          label: t('subscription.first_name'),
          id: 'firstNameInput',
          value: profileData.firstName,
          maxLength: 50,
          isInvalidHandler: getErrorMessage('firstName', profileData.firstName, t) !== null,
          isValidHandler: getErrorMessage('firstName', profileData.firstName, t) === null,
          errorTextHandler: getErrorMessage('firstName', profileData.firstName, t),
        },
        {
          type: 'text',
          key: 'lastName',
          label: t('subscription.last_name'),
          id: 'lastNameInput',
          value: profileData.lastName,
          maxLength: 50,
          isInvalidHandler: getErrorMessage('lastName', profileData.lastName, t) !== null,
          isValidHandler: getErrorMessage('lastName', profileData.lastName, t) === null,
          errorTextHandler: getErrorMessage('lastName', profileData.lastName, t),
        },
      ],
      mobileNumber: [
        {
          key: 'mobileNumber',
          label: t('my_profile.mobile_number'),
          id: 'mobileNumberInput',
          maxLength: 14,
          value: profileData.mobileNumber,
          isInvalidHandler: getErrorMessage('mobileNumber', profileData.mobileNumber, t) !== null,
          isValidHandler: getErrorMessage('mobileNumber', profileData.mobileNumber, t) === null,
          errorTextHandler: getErrorMessage('mobileNumber', profileData.mobileNumber, t),
          formatPhoneHandler: formatPhoneNumber,
        },
      ],
      emailAddress: [
        {
          type: 'email',
          key: 'emailAddress',
          label: t('my_profile.login_email_address'),
          id: 'emailAddressInput',
          maxLength: 254,
          value: profileData.emailAddress,
          isInvalidHandler: getErrorMessage('emailAddress', profileData.emailAddress, t) !== null,
          isValidHandler: getErrorMessage('emailAddress', profileData.emailAddress, t) === null,
          errorTextHandler: getErrorMessage('emailAddress', profileData.emailAddress, t),
        },
      ],
    }),
    [profileData, t]
  )

  useEffect(() => {
    const hasErrors = fieldMappings[editMode]?.some(
      (item) => getErrorMessage(item.key, profileData[item.key], t) !== null
    )

    // Explicitly set hasError to false if all fields are valid
    if (
      fieldMappings[editMode]?.every(
        (item) => getErrorMessage(item.key, profileData[item.key], t) === null
      )
    ) {
      setHasError(false)
    } else {
      setHasError(hasErrors)
    }
  }, [editMode, fieldMappings, profileData, t])

  const getFieldType = ({
    type,
    key,
    label,
    id,
    value,
    maxLength,
    isInvalidHandler,
    isValidHandler,
    errorTextHandler,
    formatPhoneHandler,
  }) => (
    <InputField
      type={type}
      value={value}
      label={label}
      onChange={(e) => onInputChange(key, e.target.value)}
      id={id}
      key={id}
      isRequired
      maxLength={maxLength}
      isInvalid={isInvalidHandler}
      isValid={isValidHandler}
      errorText={errorTextHandler}
      formatValue={formatPhoneHandler}
    />
  )

  return (
    <StyledForm id="editForm" onSubmit={(e) => handleSubmit(e, hasError, onSubmit)}>
      <h6 style={{ fontSize: '13px', marginBottom: '20px' }}>
        <span style={{ color: colors.secondary_red }}>*</span> {t('subscription.required_field')}
      </h6>

      {fieldMappings[editMode]?.map(getFieldType)}
      <ButtonContainer>
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t('buttonActions.cancel')}
        </Button>
        <Button type="Submit" form="editForm" disabled={isLoading}>
          {isLoading ? (
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
          ) : (
            t('buttonActions.save')
          )}
        </Button>
      </ButtonContainer>
    </StyledForm>
  )
}

const profileData = PropTypes.shape({
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  mobileNumber: PropTypes.string.isRequired,
  emailAddress: PropTypes.string.isRequired,
})

EditForm.propTypes = {
  editMode: PropTypes.string.isRequired,
  profileData: profileData.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}

export default EditForm
